import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ComponentChanges, SnackbarService } from '@bcl/design-system';
import {
  DrawingEngine,
  objectsProviders,
  ObjectSvg,
  panZoomToolProvider,
  PlacedObject,
  Tools,
  ToolsService,
} from '@bcl/drawing-control';
import { ReplaySubject, takeUntil } from 'rxjs';

export type ObjectPlacerCoordinatesSelectorData = {
  objects: PlacedObject[];
  objectSvg: Record<string, ObjectSvg>;
  backgroundSource?: string;
};

const defaultData: ObjectPlacerCoordinatesSelectorData = {
  objects: [],
  objectSvg: {},
};

@Component({
  selector: 'app-object-placer-coordinates-selector',
  templateUrl: './coordinates-selector.component.html',
  styleUrls: ['./coordinates-selector.component.scss'],
  providers: [objectsProviders, panZoomToolProvider],
})
export class CoordinatesSelectorComponent
  implements OnChanges, OnInit, AfterViewInit, OnDestroy
{
  @Input()
  public data: Omit<ObjectPlacerCoordinatesSelectorData, 'selectPrompt'> =
    defaultData;

  @Input()
  public selectPrompt = 'COORDINATES_SELECTOR.DEFAULT.SELECT_PROMPT';
  @Input()
  public selectedMessage = 'COORDINATES_SELECTOR.DEFAULT.SELECTED_MESSAGE';

  @Output()
  public coordinatesSelected = new EventEmitter<{ x: number; y: number }>();
  @Output()
  public viewInitialized = new EventEmitter<void>();

  public dataBound = defaultData;
  public afterViewInit = false;
  private snackbarRef: { dismiss(): void } | undefined;
  private readonly destroyed$ = new ReplaySubject<void>(1);

  constructor(
    private drawingEngine: DrawingEngine,
    private snackbarService: SnackbarService,
    private toolsService: ToolsService
  ) {}

  public ngOnChanges(
    changes: ComponentChanges<CoordinatesSelectorComponent>
  ): void {
    if (changes.data.currentValue) {
      this.bindData();
    }
  }

  public ngOnInit(): void {
    this.drawingEngine.clicked
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event) => {
        this.coordinatesSelected.emit({
          x: event.coordinates.x,
          y: event.coordinates.y,
        });
        this.snackbarRef = this.snackbarService.open(
          'green',
          this.selectedMessage ??
            'COORDINATES_SELECTOR.DEFAULT.SELECTED_MESSAGE'
        );
      });
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.afterViewInit = true;
      this.bindData();
      this.viewInitialized.emit();
    });
  }

  public ngOnDestroy(): void {
    this.snackbarRef?.dismiss();

    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private bindData(): void {
    if (!this.afterViewInit) {
      return;
    }

    this.dataBound = {
      ...defaultData,
      ...this.data,
    };
    this.toolsService.useTool(Tools.PanZoom);
  }
}
